import { useSyncExternalStore } from "react";

type Store = {
  bucket: number;
  listeners: Set<() => void>;
  timer: ReturnType<typeof setTimeout> | null;
};

const stores = new Map<number, Store>();

function getStore(intervalMs: number): Store {
  const existing = stores.get(intervalMs);

  if (existing) {
    return existing;
  }

  const store: Store = {
    bucket: Math.floor(Date.now() / intervalMs),
    listeners: new Set(),
    timer: null,
  };

  stores.set(intervalMs, store);

  return store;
}

function tick(store: Store, intervalMs: number) {
  const bucket = Math.floor(Date.now() / intervalMs);

  if (bucket === store.bucket) {
    return;
  }

  store.bucket = bucket;
  store.listeners.forEach((listener) => listener());
}

function schedule(store: Store, intervalMs: number) {
  // Fire just past the next bucket boundary, so ticks stay aligned to the
  // wall clock instead of drifting from whenever the first subscriber mounted.
  const delay = intervalMs - (Date.now() % intervalMs) + 100;

  store.timer = setTimeout(() => {
    tick(store, intervalMs);
    schedule(store, intervalMs);
  }, delay);
}

/**
 * Wall-clock time quantised to `intervalMs`, shared by every caller using the
 * same interval so a table of relative timestamps runs one timer, not one per
 * row. The value only changes when the bucket does, so subscribers on a long
 * interval do not re-render on short ticks.
 */
export function useSharedNow(intervalMs: number) {
  const store = getStore(intervalMs);

  return useSyncExternalStore(
    (onStoreChange) => {
      store.listeners.add(onStoreChange);

      if (!store.timer) {
        schedule(store, intervalMs);
      }

      // Catch up on time that passed while nothing was subscribed.
      tick(store, intervalMs);

      return () => {
        store.listeners.delete(onStoreChange);

        if (store.listeners.size === 0 && store.timer) {
          clearTimeout(store.timer);
          store.timer = null;
        }
      };
    },
    () => store.bucket,
    () => store.bucket,
  );
}
